<template>
  <!--
    Confirmation + result report of a Backup restore. Receives a plan
    { sections:{client,accounts,fields,monetary[{row,applyToBalance}],trades,notes}, requests:[…] }
    from the client workspace or a tab's bulk bar, lists what will happen
    (balance-affecting transactions highlighted in amber and gated by an
    explicit tick), runs POST /backup/restore per request and shows the
    per-item result.
  -->
  <b-modal v-model="show" size="lg" centered no-close-on-backdrop :no-close-on-esc="step === 'running'"
           :hide-header-close="step === 'running'" dialog-class="bk-cf-dialog" body-class="bk-cf-body"
           @hidden="onHidden">
    <template #modal-title>
      <template v-if="step === 'result'">
        <i class="fa-solid fa-clipboard-check mr-50 text-primary"></i>{{ $t('backup.confirm.resultTitle') }}
      </template>
      <template v-else>
        <i class="fa-solid fa-rotate-left mr-50 text-primary"></i>{{ $t('backup.confirm.title') }}
        <span v-if="plan && plan.clientName" class="text-muted font-weight-normal"> · {{ plan.clientName }}</span>
      </template>
    </template>

    <!-- ============ CONFIRM ============ -->
    <div v-if="plan && step !== 'result'">
      <div class="bk-cf-intro"><i class="fa-solid fa-shield-halved"></i>{{ $t('backup.confirm.intro') }}</div>

      <div v-if="s.client" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-solid fa-user"></i>{{ $t('backup.confirm.client') }}</div>
        <div class="bk-cf-line">
          <span class="font-weight-bold">{{ bkClientName(s.client) }}</span>
          <span class="bk-id">{{ $t('backup.backupShort') }} #{{ s.client.backupId }}</span>
          <span v-if="s.client.matchStatus === 'ID_CONFLICT'" class="bk-tag bk-tag--warn">{{ $t('backup.confirm.newId') }}</span>
        </div>
      </div>

      <div v-if="s.accounts.length" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-solid fa-id-card"></i>{{ $tc('backup.summary.accounts', s.accounts.length, { n: s.accounts.length }) }}
          <span class="bk-cf-block__sub">{{ $t('backup.confirm.accountsZero') }}</span></div>
        <div class="bk-cf-chips">
          <span v-for="a in s.accounts" :key="a.backupId" class="bk-tag">
            {{ a.backupId }} · {{ a.currency }}<template v-if="!a.willReuseId"> · {{ $t('backup.workspace.newLogin') }}</template>
          </span>
        </div>
      </div>

      <div v-if="s.fields.length" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-solid fa-code-compare"></i>{{ $tc('backup.summary.fields', s.fields.length, { n: s.fields.length }) }}
          <span class="bk-cf-block__sub">{{ $t('backup.confirm.fieldsOverwrite') }}</span></div>
        <div v-for="d in s.fields" :key="d.field" class="bk-cf-line">
          <span class="bk-cf-line__label">{{ bkFieldLabel(d.field) }}</span>
          <span class="text-muted">{{ bkFieldValue(d.field, d.currentValue) || '∅' }}</span>
          <i class="fa-solid fa-arrow-right text-primary"></i>
          <span class="font-weight-bold">{{ bkFieldValue(d.field, d.backupValue) || '∅' }}</span>
        </div>
      </div>

      <div v-if="recordOnly.length" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-solid fa-coins"></i>{{ $tc('backup.summary.monetary', recordOnly.length, { n: recordOnly.length }) }}
          <span class="bk-cf-block__sub">{{ $t('backup.confirm.recordOnly') }}</span></div>
        <div v-for="m in limited(recordOnly)" :key="m.row.backupId" class="bk-cf-line">
          <span class="bk-cf-line__label">{{ bkDateTime(m.row.registrationTime) }}</span>
          <span>{{ bkTypeLabel(m.row.type) }} · {{ bkActionLabel(m.row.action) }}</span>
          <span class="bk-mstatus" :class="`bk-mstatus--${(m.row.status || '').toLowerCase()}`">{{ m.row.status }}</span>
          <span class="bk-amt ml-auto" :class="bkAmountClass(m.row)">{{ bkMoney(m.row.amount, m.row.currency) }}</span>
        </div>
        <div v-if="recordOnly.length > limit" class="bk-cf-more">{{ $t('backup.confirm.andMore', { n: recordOnly.length - limit }) }}</div>
      </div>

      <div v-if="toBalance.length" class="bk-cf-block bk-cf-block--balance">
        <div class="bk-cf-block__head"><i class="fa-solid fa-scale-unbalanced"></i>{{ $tc('backup.confirm.balanceTitle', toBalance.length, { n: toBalance.length }) }}</div>
        <div class="bk-cf-block__warn">{{ $t('backup.confirm.balanceWarn') }}</div>
        <div v-for="m in toBalance" :key="m.row.backupId" class="bk-cf-line">
          <span class="bk-cf-line__label">{{ bkDateTime(m.row.registrationTime) }}</span>
          <span>{{ bkTypeLabel(m.row.type) }} · {{ bkActionLabel(m.row.action) }} · {{ $t('backup.columns.account') }} {{ m.row.accountId }}</span>
          <span v-if="m.row.clientName && !plan.clientName" class="text-muted">{{ m.row.clientName }}</span>
          <span class="bk-amt ml-auto" :class="bkAmountClass(m.row)">{{ bkMoney(m.row.amount, m.row.currency) }}</span>
        </div>
        <div class="bk-cf-total">
          {{ $t('backup.confirm.total') }}
          <span v-for="t in totals" :key="t.currency">{{ bkMoney(t.total, t.currency) }}</span>
        </div>
      </div>

      <div v-if="s.trades.length" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-solid fa-chart-line"></i>{{ $tc('backup.summary.trades', s.trades.length, { n: s.trades.length }) }}
          <span class="bk-cf-block__sub">{{ $t('backup.confirm.tradesHistory') }}</span></div>
        <div class="bk-cf-line">
          <span class="bk-cf-line__label">{{ $t('backup.confirm.totalProfit') }}</span>
          <span class="bk-amt" :class="tradesProfit >= 0 ? 'bk-amt--in' : 'bk-amt--out'">{{ tradesProfit.toFixed(2) }}</span>
        </div>
      </div>

      <div v-if="s.notes.length" class="bk-cf-block">
        <div class="bk-cf-block__head"><i class="fa-regular fa-note-sticky"></i>{{ $tc('backup.summary.notes', s.notes.length, { n: s.notes.length }) }}</div>
      </div>

      <div v-if="toBalance.length" class="bk-cf-ack" :class="{ 'is-on': ack }"
           @click="step !== 'running' && (ack = !ack)">
        <span @click.stop><b-form-checkbox v-model="ack" :disabled="step === 'running'"/></span>
        <span>{{ $tc('backup.confirm.ack', toBalance.length, { n: toBalance.length, total: totalsText }) }}</span>
      </div>

      <div v-if="step === 'running'" class="bk-cf-running">
        <b-spinner small variant="primary" class="mr-50"/>
        {{ $t('backup.confirm.running', { i: progress, n: plan.requests.length }) }}
      </div>
    </div>

    <!-- ============ RESULT ============ -->
    <div v-else-if="step === 'result'">
      <div class="bk-cf-score">
        <div class="bk-cf-score__item bk-cf-score__item--restored"><b>{{ count('RESTORED') }}</b>{{ $t('backup.result.RESTORED') }}</div>
        <div class="bk-cf-score__item bk-cf-score__item--skipped"><b>{{ count('SKIPPED') }}</b>{{ $t('backup.result.SKIPPED') }}</div>
        <div class="bk-cf-score__item bk-cf-score__item--failed"><b>{{ count('FAILED') }}</b>{{ $t('backup.result.FAILED') }}</div>
      </div>
      <div class="bk-cf-results">
        <div v-for="(it, i) in results" :key="i" class="bk-cf-res" :class="`bk-cf-res--${(it.result || '').toLowerCase()}`">
          <i :class="kindIcon(it.kind)" class="bk-cf-res__icon"></i>
          <div class="bk-cf-res__main">
            <div class="bk-cf-res__title">
              {{ bkT(`backup.kind.${it.kind}`, it.kind) }}
              <span v-if="it.backupId" class="bk-id">#{{ it.backupId }}</span>
              <span v-if="it.currentId" class="bk-id bk-id--current">→ #{{ it.currentId }}</span>
            </div>
            <div v-if="it.message" class="bk-cf-res__msg">{{ it.message }}</div>
          </div>
          <span class="bk-result" :class="`bk-result--${(it.result || '').toLowerCase()}`">{{ bkT(`backup.result.${it.result}`, it.result) }}</span>
        </div>
        <div v-if="!results.length" class="text-muted text-center p-1">{{ $t('backup.confirm.noItems') }}</div>
      </div>
    </div>

    <template #modal-footer>
      <template v-if="step === 'result'">
        <b-button v-if="clientIds.length === 1" variant="outline-primary" @click="showClient(clientIds[0])">
          <i class="fa-solid fa-arrow-up-right-from-square mr-50"></i>{{ $t('backup.openCurrentClient') }} #{{ clientIds[0] }}
        </b-button>
        <b-button variant="primary" @click="show = false">{{ $t('backup.close') }}</b-button>
      </template>
      <template v-else>
        <b-button variant="link" class="bk-ghost" :disabled="step === 'running'" @click="show = false">{{ $t('backup.cancel') }}</b-button>
        <b-button :variant="toBalance.length ? 'warning' : 'primary'" :disabled="!canRun" @click="run">
          <b-spinner v-if="step === 'running'" small class="mr-50"/>
          <i v-else class="fa-solid fa-rotate-left mr-50"></i>
          {{ toBalance.length ? $t('backup.confirm.goBalance') : $t('backup.confirm.go') }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBackup, { balanceTotals, KIND_ICONS } from '@/components/mixin/mixinBackup'

const EMPTY = { client: null, accounts: [], fields: [], monetary: [], trades: [], notes: [] }

export default {
  name: 'BackupRestoreConfirm',
  mixins: [mixinNotifications, mixinBackup],
  data: () => ({
    show: false,
    plan: null,
    step: 'confirm', // confirm | running | result
    ack: false,
    progress: 0,
    results: [],
    clientIds: [],
    limit: 8,
  }),
  computed: {
    s() {
      return { ...EMPTY, ...((this.plan && this.plan.sections) || {}) }
    },
    recordOnly() {
      return this.s.monetary.filter(m => !m.applyToBalance)
    },
    toBalance() {
      return this.s.monetary.filter(m => m.applyToBalance)
    },
    totals() {
      return balanceTotals(this.toBalance)
    },
    totalsText() {
      return this.totals.map(t => this.bkMoney(t.total, t.currency)).join(' + ')
    },
    tradesProfit() {
      return this.s.trades.reduce((t, x) => t + (Number(x.profit) || 0), 0)
    },
    canRun() {
      return this.step === 'confirm' && !!this.plan && this.plan.requests.length > 0
        && (!this.toBalance.length || this.ack)
    },
  },
  methods: {
    ...mapActions('backup', ['restoreBackup']),
    open(plan) {
      this.plan = plan
      this.step = 'confirm'
      this.ack = false
      this.progress = 0
      this.results = []
      this.clientIds = []
      this.show = true
    },
    limited(list) {
      return list.slice(0, this.limit)
    },
    kindIcon(kind) {
      return KIND_ICONS[kind] || 'fa-solid fa-circle-exclamation'
    },
    count(result) {
      return this.results.filter(r => r.result === result).length
    },
    async run() {
      if (!this.canRun) return
      this.step = 'running'
      const results = []
      const clientIds = []
      for (let i = 0; i < this.plan.requests.length; i++) {
        this.progress = i + 1
        const req = this.plan.requests[i]
        try {
          // Sequential on purpose: each request is independent server-side and
          // a client restore must land before its items.
          const res = await this.restoreBackup(req)
          ;((res && res.items) || []).forEach(it => results.push(it))
          if (res && res.clientId != null && !clientIds.includes(res.clientId)) clientIds.push(res.clientId)
        } catch (err) {
          results.push({
            kind: req.restoreClient ? 'CLIENT' : 'REQUEST',
            backupId: req.clientBackupId,
            result: 'FAILED',
            message: this.getErrorDescription(err),
          })
        }
      }
      this.results = results
      this.clientIds = clientIds
      this.step = 'result'
      const failed = results.filter(r => r.result === 'FAILED').length
      const restored = results.filter(r => r.result === 'RESTORED').length
      if (failed) {
        this.showWarningNotification(this.$t('backup.confirm.doneWithErrors', { restored, failed }))
      } else {
        this.showSuccessNotification(this.$t('backup.confirm.done', { restored }))
      }
      this.$emit('done', { plan: this.plan, results, clientIds })
    },
    onHidden() {
      if (this.step === 'running') return
      this.plan = null
      this.results = []
    },
  },
}
</script>
